/**
 * AI Service Layer for the gateway.
 *
 * All LLM calls go through the Go backend at /api/ai/chat[/stream].
 * The server holds the API key, endpoint, and model.
 */

import { getCoreUrl, getAIConfig } from "./config";

const createIdempotencyKey = () => {
  const secureCrypto = window.crypto;
  if (typeof secureCrypto?.randomUUID === "function") {
    return secureCrypto.randomUUID();
  }
  if (typeof secureCrypto?.getRandomValues === "function") {
    const bytes = secureCrypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  throw new Error("Secure random generation is unavailable");
};

const isRetryableStatusCode = (status) =>
  status === 408 || status === 425 || status >= 500;

const getErrorMessageFromBody = (data) => {
  if (!data || typeof data !== "object") return null;
  if (typeof data.error?.message === "string" && data.error.message.trim())
    return data.error.message.trim();
  if (typeof data.error === "string" && data.error.trim()) return data.error.trim();
  if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
  return null;
};

const readAIError = async (response) => {
  const fallback = response.statusText || "Request failed";
  const rawBody = await response.text().catch(() => "");
  const body = rawBody.trim();
  if (!body) return { message: fallback, data: null };
  try {
    const data = JSON.parse(body);
    return { message: getErrorMessageFromBody(data) || body || fallback, data };
  } catch {
    return { message: body || fallback, data: null };
  }
};

// managedAIErrorMessage — managed-provider errors carry an operator-ready
// message (and a reset date for quota exhaustion); show that verbatim instead
// of burying it behind an "AI API error (429)" prefix.
const managedAIErrorMessage = (message, code, resetAt) => {
  if (!code || !code.startsWith("managed_ai_")) return null;
  let text = message || "Control Seat AI is temporarily unavailable.";
  if (code === "managed_ai_quota_exhausted" && resetAt) {
    const when = new Date(resetAt);
    if (!Number.isNaN(when.getTime())) {
      text += ` Allowance resets ${when.toLocaleDateString(undefined, { month: "long", day: "numeric", timeZone: "UTC" })}.`;
    }
  }
  return text;
};

const createAIHTTPError = async (response, label = "AI API error") => {
  const { message, data } = await readAIError(response);
  const metadata = data?.error && typeof data.error === "object" ? data.error : data;
  const aiCode = typeof metadata?.code === "string" ? metadata.code : "";
  const resetAt = typeof metadata?.reset_at === "string" ? metadata.reset_at : "";
  const managed = managedAIErrorMessage(message, aiCode, resetAt);
  const error = new Error(managed || `${label} (${response.status}): ${message}`);
  error.status = response.status;
  error.httpStatus = response.status;
  error.aiCode = aiCode;
  error.resetAt = resetAt;
  return error;
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = 180000) => {
  // If the caller supplied an AbortSignal, chain it with our timeout one so
  // either source cancels the fetch.
  const controller = new AbortController();
  const externalSignal = options.signal;
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

export const callLLM = async (messages, options = {}) => {
  const config = getAIConfig();
  const url = `${getCoreUrl()}/api/ai/chat`;
  const idempotencyKey = createIdempotencyKey();
  const requestBody = {
    messages,
    temperature: options.temperature ?? config.temperature,
    max_tokens: options.maxTokens || config.maxTokens,
  };
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const response = await fetchWithTimeout(
        url,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": idempotencyKey,
          },
          credentials: "include",
          body: JSON.stringify(requestBody),
          signal: options.signal,
        },
        options.timeout || config.timeout
      );
      if (!response.ok) {
        const error = await createAIHTTPError(response);
        if (attempt === 0 && isRetryableStatusCode(response.status)) continue;
        throw error;
      }
      const data = await response.json();
      if (!data.choices || data.choices.length === 0) {
        throw new Error("No response from AI API");
      }
      return data.choices[0].message.content;
    } catch (error) {
      if (error.name === "AbortError") {
        if (attempt === 0) continue;
        throw new Error("AI request timed out");
      }
      throw error;
    }
  }
  throw new Error("AI request failed");
};

/**
 * Stream LLM responses as an async generator.
 * Each yielded value is one OpenAI-compatible ChatCompletionChunk.
 */
export async function* streamLLM({ messages, tools, temperature, maxTokens, signal } = {}) {
  const config = getAIConfig();
  const idempotencyKey = createIdempotencyKey();
  const body = {
    messages,
    temperature: temperature ?? config.temperature,
    max_tokens: maxTokens || config.maxTokens,
  };
  if (Array.isArray(tools) && tools.length > 0) body.tools = tools;

  const response = await fetch(`${getCoreUrl()}/api/ai/chat/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      "Idempotency-Key": idempotencyKey,
    },
    credentials: "include",
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    throw await createAIHTTPError(response, "AI stream error");
  }
  if (!response.body) throw new Error("AI stream error: response has no body");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const parseFrame = (frame) => {
    const chunks = [];
    for (const line of frame.split("\n")) {
      if (!line.startsWith("data: ")) continue;
      const data = line.slice(6);
      if (data === "[DONE]") return { chunks, done: true };
      try {
        chunks.push(JSON.parse(data));
      } catch {
        // Ignore malformed records; the missing terminal marker still makes
        // a truncated stream fail closed below.
      }
    }
    return { chunks, done: false };
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      if (buffer.indexOf("\r") !== -1) buffer = buffer.replace(/\r\n/g, "\n");

      let sepIdx;
      while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        const { chunks, done: sawDone } = parseFrame(frame);
        for (const chunk of chunks) yield chunk;
        if (sawDone) return;
      }
    }

    buffer += decoder.decode();
    const remainder = buffer.replace(/\r\n/g, "\n").trim();
    if (remainder) {
      const { chunks, done: sawDone } = parseFrame(remainder);
      for (const chunk of chunks) yield chunk;
      if (sawDone) return;
    }
    throw new Error("AI stream error: stream ended before [DONE]");
  } finally {
    try {
      await reader.cancel();
    } catch {}
  }
}
